import { useState } from "react";

import type { ApiClient, SchemaDiagnostic } from "../../shared/api/client";
import { useT } from "../../shared/i18n";
import { Button, Dialog, useToast } from "../../shared/ui";
import {
  incompatibleChanges,
  publishDraft,
  publishRefusal,
  REFUSAL_MESSAGES,
  type DraftState,
} from "./controls";
import { isStaleDraftRevision } from "./interfaceDraft";

/**
 * Confirm and publish a Draft. Names how many changes it carries and whether
 * existing records will be filled, then reports the new version number.
 */
export function DraftPublishDialog({
  open,
  onOpenChange,
  api,
  abilityId,
  actorId,
  draft,
  changeCount,
  hasBackfill,
  reason,
  onPublished,
  onStaleRevision,
  onBlocked,
  onFailed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  api: ApiClient;
  abilityId: string;
  actorId: string;
  draft: Pick<DraftState, "manifest" | "draftRevision">;
  /** Entries of the change list; `null` when the Draft has no base to compare with. */
  changeCount: number | null;
  hasBackfill: boolean;
  /** `rollback` for a Draft restored from a prior version. */
  reason?: "user_confirmed" | "rollback";
  onPublished: () => void | Promise<void>;
  /** The Draft moved under the reviewer (revision mismatch). */
  onStaleRevision: () => void;
  /** The Draft would break stored data: the parts it would break. */
  onBlocked: (changes: SchemaDiagnostic[]) => void;
  /** Any other refusal or failure, as text for the page. */
  onFailed: (message: string) => void;
}) {
  const t = useT();
  const toast = useToast();
  const [running, setRunning] = useState(false);

  const publish = () => {
    setRunning(true);
    void publishDraft(api, abilityId, actorId, {
      ...draft,
      reason,
      status: "preview",
    })
      .then(async (published) => {
        const version = published.versionNumber;
        toast.show(
          version === null || version === undefined
            ? t("draft.published")
            : `${t("draft.published")} ${String(version)}`,
          "success",
        );
        await onPublished();
      })
      .catch((cause: unknown) => {
        const blocked = incompatibleChanges(cause);
        const refusal = publishRefusal(cause);
        if (isStaleDraftRevision(cause)) onStaleRevision();
        else if (blocked) onBlocked(blocked);
        else if (refusal && refusal !== "incompatible")
          onFailed(t(REFUSAL_MESSAGES[refusal]));
        else
          onFailed(
            cause instanceof Error ? cause.message : t("draft.publishFailed"),
          );
      })
      .finally(() => {
        setRunning(false);
        onOpenChange(false);
      });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!running) onOpenChange(next);
      }}
      title={t("draft.publishConfirm.title")}
      description={
        <>
          {t("draft.publishConfirm.body")}
          {changeCount !== null && (
            <>
              {" "}
              {t("draft.publishConfirm.count")}: {changeCount}.
            </>
          )}
          {hasBackfill && <> {t("draft.publishConfirm.backfill")}</>}
        </>
      }
      footer={
        <>
          <Button
            variant="secondary"
            disabled={running}
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t("record.cancel")}
          </Button>
          <Button loading={running} onClick={publish}>
            {t("draft.publish")}
          </Button>
        </>
      }
    />
  );
}
